/**
 * Module → Entity pickers for the Business Rule form (DSL `ui.customEditor: RuleModulePicker`
 * / `RuleEntityPicker`). Both read the rule-context registry: only an entity with a declared
 * rule context can carry a tenant rule, because the condition editor's fields and the engine's
 * input come from that context. Module narrows Entity, as on the Field Restrictions page.
 * Once a rule exists its target is fixed — the condition's field paths are entity-bound — so
 * both pickers render read-only on an existing rule.
 */

import { useMemo } from 'react';
import { rankWith, ControlProps, isControl, UISchemaElement } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { CACHE_TIME } from '@/config/queryDefaults';
import { ruleService } from '@/services/core/RuleService';
import { entitiesIn, modulesOf } from '@/core/rules/ruleScope';

export const RULE_MODULE_PICKER = 'RuleModulePicker';
export const RULE_ENTITY_PICKER = 'RuleEntityPicker';

const customRendererOf = (uischema: UISchemaElement): string | undefined =>
  (uischema as { options?: { customRenderer?: string } }).options?.customRenderer;

export const ruleModulePickerTester = rankWith(
  20,
  (uischema) => isControl(uischema) && customRendererOf(uischema) === RULE_MODULE_PICKER
);

export const ruleEntityPickerTester = rankWith(
  20,
  (uischema) => isControl(uischema) && customRendererOf(uischema) === RULE_ENTITY_PICKER
);

function useRuleContextEntities() {
  return useQuery({
    queryKey: ['rule-context-entities'],
    queryFn: () => ruleService.getContextEntities(),
    staleTime: CACHE_TIME.METADATA,
  });
}

/** The form's own record: an `id` means the rule already exists and its target is fixed. */
function useRuleFormData(): Record<string, unknown> {
  const ctx = useJsonForms();
  return (ctx.core?.data as Record<string, unknown> | undefined) ?? {};
}

const RuleModulePickerComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, required, enabled, errors } = props;
  const { t } = useTranslation();
  const formData = useRuleFormData();
  const { data: entities = [], isLoading } = useRuleContextEntities();
  const modules = useMemo(() => modulesOf(entities), [entities]);
  const locked = Boolean(formData.id);
  const value = typeof data === 'string' ? data : '';
  const hasError = Boolean(errors);

  return (
    <FormControl fullWidth size="small" required={required} disabled={!enabled || locked || isLoading} error={hasError}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={modules.includes(value) || value === '' ? value : ''}
        label={label}
        onChange={(e) => {
          handleChange(path, e.target.value || undefined);
          // A module change invalidates the entity choice: entities belong to one module.
          if (formData.entity) handleChange('entity', undefined);
        }}
      >
        {modules.map((m) => (
          <MenuItem key={m} value={m}>{m}</MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {hasError ? errors : locked ? t('businessRules.scope.lockedAfterCreate') : ' '}
      </FormHelperText>
    </FormControl>
  );
};

const RuleEntityPickerComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, required, enabled, errors } = props;
  const { t } = useTranslation();
  const formData = useRuleFormData();
  const module = typeof formData.module === 'string' ? formData.module : undefined;
  const { data: entities = [], isLoading } = useRuleContextEntities();
  const options = useMemo(() => entitiesIn(entities, module), [entities, module]);
  const locked = Boolean(formData.id);
  const value = typeof data === 'string' ? data : '';
  const hasError = Boolean(errors);
  const known = options.some((e) => e.entityName === value);

  return (
    <FormControl fullWidth size="small" required={required} disabled={!enabled || locked || isLoading || !module} error={hasError}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={known || value === '' ? value : ''}
        label={label}
        onChange={(e) => handleChange(path, e.target.value || undefined)}
      >
        {options.map((e) => (
          <MenuItem key={e.entityName} value={e.entityName}>{e.entityName}</MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {hasError
          ? errors
          : locked
            ? t('businessRules.scope.lockedAfterCreate')
            : !module
              ? t('businessRules.scope.selectModuleFirst')
              : ' '}
      </FormHelperText>
    </FormControl>
  );
};

export const RuleModulePickerRenderer = withJsonFormsControlProps(RuleModulePickerComponent);
export const RuleEntityPickerRenderer = withJsonFormsControlProps(RuleEntityPickerComponent);

export const ruleModulePickerEntry = { tester: ruleModulePickerTester, renderer: RuleModulePickerRenderer };
export const ruleEntityPickerEntry = { tester: ruleEntityPickerTester, renderer: RuleEntityPickerRenderer };
